import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "@aviary";
import { PageFooter } from "@aviary/layouts";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useFsAnalytics } from "@shared/hooks/useFsAnalytics/useFsAnalytics";
import { useParams, useLocation } from "@shared/react-router-dom/react-router-dom";
import { SwiftFillSteps } from "@shared/types/SwiftFillSteps";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { JourneyAvailabilityForm } from "@unauthenticated/signupSignin/components/JourneyAvailabilityForm/JourneyAvailabilityForm";
import { JourneyCheckoutHeaderNav } from "@unauthenticated/signupSignin/components/JourneyCheckoutHeaderNav/JourneyCheckoutHeaderNav";
import { JourneyCheckoutLayout } from "@unauthenticated/signupSignin/components/JourneyCheckoutLayout/JourneyCheckoutLayout";
import { JourneyEmailFormContent } from "@unauthenticated/signupSignin/components/JourneyEmailFormContent/JourneyEmailFormContent";
import { JourneyLoginForm } from "@unauthenticated/signupSignin/components/JourneyLoginForm/JourneyLoginForm";
import { JourneyMobileOrderSummary } from "@unauthenticated/signupSignin/components/JourneyMobileOrderSummary/JourneyMobileOrderSummary";
import { JourneyOrderSummary } from "@unauthenticated/signupSignin/components/JourneyOrderSummary/JourneyOrderSummary";
import { JourneyPatientInfoForm } from "@unauthenticated/signupSignin/components/JourneyPatientInfoForm/JourneyPatientInfoForm";
import { JourneySuccessContent } from "@unauthenticated/signupSignin/components/JourneySuccessContent/JourneySuccessContent";
import { useJourneyWaitlistAddUnauthenticatedSignUpSignInMutation } from "@unauthenticated/signupSignin/components/JourneyWaitlistModal/JourneyWaitlistAdd.mutation.generated";
import JourneyWaitlistModal, {
  type WaitlistReason,
} from "@unauthenticated/signupSignin/components/JourneyWaitlistModal/JourneyWaitlistModal";
import { useCheckJourneyAvailabilityUnauthenticatedSignUpSignInMutation } from "@unauthenticated/signupSignin/data/mutations/CheckJourneyAvailability.mutation.generated";
import { usePublicJourneyPackageUnauthenticatedSignUpSignInQuery } from "@unauthenticated/signupSignin/data/queries/PublicJourneyPackageQuery/PublicJourneyPackage.query.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { usePatientNextLoginStepUnauthenticatedSignUpSignInMutation } from "../../scenes/PatientAuthStartPage/EmailCheckForm/PatientNextLoginStep.mutation.generated";

import * as styles from "./JourneyCheckoutPage.styles";

// URL parameter constants
const URL_PARAMS = {
  EMAIL: "email",
  NEXT_STEP: "nextStep",
  MAGIC_LINK_PAGE: "magic_link_page",
  MAGIC_LINK_EMAIL: "magic_link_email",
} as const;

// Magic link page constants
const MAGIC_LINK_PAGES = {
  EMAIL: "email",
  SUCCESS: "success",
} as const;

type MagicLinkPage = (typeof MAGIC_LINK_PAGES)[keyof typeof MAGIC_LINK_PAGES];

interface JourneyCheckoutPageProps {
  journeyPackageId: string;
  storeId: string;
}

interface LoginState {
  email: string;
  nextStep: SwiftFillSteps;
}

interface PatientInfoState {
  email: string;
}

interface MagicLinkState {
  email?: string;
  page: MagicLinkPage;
}

const JourneyCheckoutPage = ({ journeyPackageId, storeId }: JourneyCheckoutPageProps) => {
  const { phoneLarge } = useBreakpoints();
  const { makeToast } = useToast();
  const { t } = useTranslation();
  const location = useLocation();
  const logAnalyticsEvent = useAnalyticsLogger();
  const { logEvent } = useFsAnalytics();

  const { storeSlug: routeStoreSlug } = useParams<{ storeSlug: string }>();

  // Extract storeSlug manually from pathname since route params aren't configured
  // URL format: /checkout/wzaius/journey/TGFiczo6Sm91cm5leXM6OlBhY2thZ2UtMQ==
  const storeSlug = (() => {
    const pathParts = location.pathname.split("/");
    // Expected: ['', 'checkout', 'wzaius', 'journey', 'package-id']
    if (pathParts.length >= 3 && pathParts[1] === "checkout") {
      return pathParts[2]; // 'wzaius'
    }

    return routeStoreSlug; // fallback to route param if available
  })();

  const { data: journeyPackageData, loading: journeyPackageLoading } =
    usePublicJourneyPackageUnauthenticatedSignUpSignInQuery({
      variables: { id: journeyPackageId, journeysSlug: storeSlug },
    });

  const journeyPackage = journeyPackageData?.viewer?.journeyPackage as
    | { totalPrice?: number; practitionerName?: string; name?: string }
    | undefined;

  const dispensaryName =
    journeyPackageData?.viewer?.labsJourneysOfferPage?.displayName ??
    journeyPackage?.practitionerName ??
    t(l.journeyCheckout.waitlist.dispensaryNameFallback);

  const analyticsPractitionerName = journeyPackage?.practitionerName;
  const analyticsPanelName = journeyPackage?.name;
  const analyticsPanelSubtotal = journeyPackage?.totalPrice;

  // Restore login state from URL parameters using native URLSearchParams
  const getInitialLoginState = (): LoginState | null => {
    const params = new URLSearchParams(location.search);
    const email = params.get(URL_PARAMS.EMAIL);
    const nextStep = params.get(URL_PARAMS.NEXT_STEP) as SwiftFillSteps;

    if (email && nextStep) {
      return { email, nextStep };
    }

    return null;
  };

  // Magic link state from URL parameters (following same pattern as loginState)
  const getInitialMagicLinkState = (): MagicLinkState | null => {
    const params = new URLSearchParams(location.search);
    const magicLinkPage = params.get(URL_PARAMS.MAGIC_LINK_PAGE) as MagicLinkPage;
    const magicLinkEmail = params.get(URL_PARAMS.MAGIC_LINK_EMAIL);

    if (
      magicLinkPage &&
      (magicLinkPage === MAGIC_LINK_PAGES.EMAIL || magicLinkPage === MAGIC_LINK_PAGES.SUCCESS)
    ) {
      return {
        page: magicLinkPage,
        email: magicLinkEmail || undefined,
      };
    }

    return null;
  };

  const updateUrlWithMagicLinkState = (state: MagicLinkState | null) => {
    const params = new URLSearchParams(location.search);

    // Remove existing magic link params
    params.delete(URL_PARAMS.MAGIC_LINK_PAGE);
    params.delete(URL_PARAMS.MAGIC_LINK_EMAIL);

    // Add new state if provided
    if (state) {
      params.set(URL_PARAMS.MAGIC_LINK_PAGE, state.page);
      if (state.email) {
        params.set(URL_PARAMS.MAGIC_LINK_EMAIL, state.email);
      }
    }

    // Update URL without triggering navigation
    const queryString = params.toString();
    const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  };

  const [loginState, setLoginState] = useState<LoginState | null>(getInitialLoginState());
  const [patientInfoState, setPatientInfoState] = useState<PatientInfoState | null>(null);
  const [magicLinkState, setMagicLinkState] = useState<MagicLinkState | null>(
    getInitialMagicLinkState()
  );

  // Store current form values for use in mutations
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [currentZipCode, setCurrentZipCode] = useState("");
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [waitlistReason, setWaitlistReason] = useState<WaitlistReason | null>(null);

  const hasLoggedLocationView = useRef(false);
  const hasLoggedCheckoutStart = useRef(false);
  const hasLoggedPatientView = useRef(false);
  const hasLoggedSigninView = useRef(false);

  const [checkJourneyAvailability, { loading: availabilityLoading }] =
    useCheckJourneyAvailabilityUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        const availabilityResult = data?.labsJourneysCheckForJourneysAvailability;

        if (availabilityResult?.errors?.message) {
          makeToast("error", availabilityResult.errors.message);

          return;
        }

        if (availabilityResult?.errors?.fields) {
          const { fields } = availabilityResult.errors;

          if (fields.packageId && fields.packageId.length > 0) {
            setIsWaitlistModalOpen(true);

            if (fields.packageId.includes("Journeys package is at full capacity")) {
              setWaitlistReason("order_limit");
            } else if (fields.packageId.includes("is not currently available")) {
              setWaitlistReason("store_not_ready");
            }

            return;
          }

          if (fields.zipcode && fields.zipcode.length > 0) {
            makeToast(
              "error",
              `${t(l.journeyCheckout.availability.errors.zipCodePrefix)} ${fields.zipcode.join(", ")}`
            );

            return;
          }
        }

        if (!availabilityResult?.available) {
          setIsWaitlistModalOpen(true);
          setWaitlistReason("zipcode");

          return;
        }

        // Availability check passed, proceed to user check with recaptcha
        gRecaptchaExecute((captchaToken: string) => {
          checkPatientNextLoginStep({
            variables: {
              input: {
                attributes: {
                  email: currentUserEmail,
                  redirectPath: `/r/checkout/${storeSlug}/journey/${journeyPackageId}`,
                  storeId,
                },
                captchaToken,
              },
            },
          });
        });
      },
      onError: error => {
        makeToast("error", error?.message || t(l.journeyCheckout.availability.errors.generalError));
      },
    });

  const [addToWaitlist, { loading: addToWaitlistLoading }] =
    useJourneyWaitlistAddUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        const isJoined = data?.labsJourneysJoinWaitlist?.joined;

        if (isJoined) {
          makeToast("success", t(l.journeyCheckout.waitlist.successToast));
        } else {
          makeToast("error", t(l.journeyCheckout.waitlist.errorToast));
        }
      },
      onError: error => {
        makeToast("error", error?.message || t(l.journeyCheckout.availability.errors.generalError));
      },
    });

  const [checkPatientNextLoginStep, { loading: userCheckLoading }] =
    usePatientNextLoginStepUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        const userCheckResult = data?.auth?.patientNextLoginStep;

        if (userCheckResult?.errors?.message) {
          makeToast("error", userCheckResult.errors.message);

          return;
        }

        const nextStep = userCheckResult?.nextStep;

        void logAnalyticsEvent({
          variables: {
            input: {
              eventName: AnalyticsEvent.JOURNEY_CHECKOUT_LOCATION_SUBMIT,
              payload: {
                new_user: nextStep === SwiftFillSteps.CREATE_ACCOUNT,
                panel_selection: analyticsPanelName ?? "",
                panel_subtotal:
                  analyticsPanelSubtotal !== undefined
                    ? `$${analyticsPanelSubtotal.toFixed(2)}`
                    : "",
              },
            },
          },
        });

        switch (nextStep) {
          case SwiftFillSteps.CREATE_ACCOUNT:
            // User doesn't exist - show patient info form for comprehensive signup
            setPatientInfoState({
              email: currentUserEmail,
            });
            break;
          case SwiftFillSteps.PASSWORD:
            // User exists - show login form
            setLoginState({
              email: currentUserEmail,
              nextStep,
            });
            break;
          case SwiftFillSteps.MAGIC_LINK:
            // Magic link already sent - show magic success state
            setMagicLinkState({
              email: currentUserEmail,
              page: MAGIC_LINK_PAGES.SUCCESS,
            });
            break;
          default:
            // Handle other cases (userNotFound, authenticationLocked, etc.)
            makeToast("error", t(l.journeyCheckout.availability.errors.generalError));
        }
      },
      onError: error => {
        makeToast("error", error?.message || t(l.journeyCheckout.availability.errors.generalError));
      },
    });

  const authenticatedRedirectPath = useMemo(
    () => `/r/checkout/${storeSlug}/journey/${journeyPackageId}`,
    [storeSlug, journeyPackageId]
  );

  const isMobile = !phoneLarge.greaterThan;
  const orderTotal = journeyPackage?.totalPrice ?? 0;
  const isProcessing = availabilityLoading || userCheckLoading;

  const checkoutStartSessionKey = `journey_checkout_start_${journeyPackageId}`;

  // Fire checkout.start once when analytics data is available.
  // Uses sessionStorage to deduplicate across the unauth→auth page transition.
  useEffect(() => {
    if (
      hasLoggedCheckoutStart.current ||
      sessionStorage.getItem(checkoutStartSessionKey) ||
      !analyticsPanelName ||
      !analyticsPractitionerName ||
      analyticsPanelSubtotal === undefined
    ) {
      return;
    }

    logEvent().checkoutOrderStart({});
    void logAnalyticsEvent({
      variables: {
        input: {
          eventName: AnalyticsEvent.JOURNEY_CHECKOUT_START,
          payload: {
            practitioner: analyticsPractitionerName,
            panel_selection: analyticsPanelName,
            panel_subtotal: `$${analyticsPanelSubtotal.toFixed(2)}`,
          },
        },
      },
    });
    sessionStorage.setItem(checkoutStartSessionKey, "true");
    hasLoggedCheckoutStart.current = true;
  }, [
    analyticsPanelName,
    analyticsPractitionerName,
    analyticsPanelSubtotal,
    checkoutStartSessionKey,
    logAnalyticsEvent,
  ]);

  // Fire location.view when on the availability step
  useEffect(() => {
    const isOnAvailabilityView = !loginState && !patientInfoState && !magicLinkState;

    if (
      isOnAvailabilityView &&
      !hasLoggedLocationView.current &&
      analyticsPanelName &&
      analyticsPractitionerName &&
      analyticsPanelSubtotal !== undefined
    ) {
      void logAnalyticsEvent({
        variables: {
          input: {
            eventName: AnalyticsEvent.JOURNEY_CHECKOUT_LOCATION_VIEW,
            payload: {
              practitioner: analyticsPractitionerName,
              panel_selection: analyticsPanelName,
              panel_subtotal: `$${analyticsPanelSubtotal.toFixed(2)}`,
            },
          },
        },
      });
      hasLoggedLocationView.current = true;
    }
  }, [
    analyticsPanelName,
    analyticsPractitionerName,
    analyticsPanelSubtotal,
    loginState,
    patientInfoState,
    magicLinkState,
    logAnalyticsEvent,
  ]);

  // Fire patient.view when entering patient info step
  useEffect(() => {
    if (
      !patientInfoState ||
      hasLoggedPatientView.current ||
      !analyticsPanelName ||
      !analyticsPractitionerName ||
      analyticsPanelSubtotal === undefined
    ) {
      return;
    }

    void logAnalyticsEvent({
      variables: {
        input: {
          eventName: AnalyticsEvent.JOURNEY_CHECKOUT_PATIENT_VIEW,
          payload: {
            practitioner: analyticsPractitionerName,
            panel_selection: analyticsPanelName,
            panel_subtotal: `$${analyticsPanelSubtotal.toFixed(2)}`,
          },
        },
      },
    });
    hasLoggedPatientView.current = true;
  }, [
    patientInfoState,
    analyticsPanelName,
    analyticsPractitionerName,
    analyticsPanelSubtotal,
    logAnalyticsEvent,
  ]);

  // Fire signin.view when entering login step
  useEffect(() => {
    if (
      !loginState ||
      hasLoggedSigninView.current ||
      !analyticsPanelName ||
      !analyticsPractitionerName ||
      analyticsPanelSubtotal === undefined
    ) {
      return;
    }

    void logAnalyticsEvent({
      variables: {
        input: {
          eventName: AnalyticsEvent.JOURNEY_CHECKOUT_SIGNIN_VIEW,
          payload: {
            practitioner: analyticsPractitionerName,
            panel_selection: analyticsPanelName,
            panel_subtotal: `$${analyticsPanelSubtotal.toFixed(2)}`,
          },
        },
      },
    });
    hasLoggedSigninView.current = true;
  }, [
    loginState,
    analyticsPanelName,
    analyticsPractitionerName,
    analyticsPanelSubtotal,
    logAnalyticsEvent,
  ]);

  const handleContinue = (userEmail: string, userZipCode: string) => {
    // Store values for use in mutation callbacks
    setCurrentUserEmail(userEmail);
    setCurrentZipCode(userZipCode);

    // Start the availability check
    checkJourneyAvailability({
      variables: {
        input: {
          attributes: {
            packageId: journeyPackageId,
            zipcode: userZipCode,
          },
        },
      },
    });
  };

  const handleLoginSuccess = () => {
    // TODO: Handle successful login - proceed to checkout or next step
    // This might involve redirecting to the actual checkout with authentication
    setLoginState(null);
  };

  const handlePatientCreated = () => {
    // TODO: Handle successful patient creation - proceed to checkout or next step
    // This might involve redirecting to the actual checkout with authentication
    setPatientInfoState(null);
  };

  // Wrapper function to update magic link state and persist to URL
  const updateMagicLinkState = (state: MagicLinkState | null) => {
    setMagicLinkState(state);
    updateUrlWithMagicLinkState(state);
  };

  const handleBackToPLP = () => {
    window.location.href = sharedRoutes.journeyWebsite(storeSlug || "");
  };

  const handleBackToAvailability = () => {
    setLoginState(null);
    setPatientInfoState(null);
    updateMagicLinkState(null);
  };

  const handleBackNavigation = () => {
    const checkoutPage = patientInfoState ? "Patient info" : "Location";

    void logAnalyticsEvent({
      variables: {
        input: {
          eventName: AnalyticsEvent.JOURNEY_CHECKOUT_BACK,
          payload: {
            checkout_page: checkoutPage,
            practitioner: analyticsPractitionerName || "",
            panel_selection: analyticsPanelName || "",
            panel_subtotal:
              analyticsPanelSubtotal !== undefined ? `$${analyticsPanelSubtotal.toFixed(2)}` : "",
          },
        },
      },
    });

    // If we're in magic link flow and there's a login state, go back to login
    if (magicLinkState && loginState) {
      updateMagicLinkState(null);

      return;
    }

    // If we're coming back from patient info form, preserve the email/zipCode but clear patient info state
    if (patientInfoState) {
      setPatientInfoState(null);
      // Keep currentUserEmail and currentZipCode so availability form can be pre-filled

      return;
    }

    // Otherwise, go back to availability (clears all states)
    handleBackToAvailability();
  };

  const handleMagicLinkClick = () => {
    updateMagicLinkState({
      page: MAGIC_LINK_PAGES.EMAIL,
      email: loginState?.email,
    });
  };

  const handleMagicLinkSuccess = () => {
    setMagicLinkState(prev => {
      const newState = prev ? { ...prev, page: MAGIC_LINK_PAGES.SUCCESS } : null;
      updateUrlWithMagicLinkState(newState);

      return newState;
    });
  };

  const handleMagicLinkEmailChange = (email: string) => {
    setMagicLinkState(prev => {
      const newState = prev ? { ...prev, email } : { email, page: MAGIC_LINK_PAGES.EMAIL };
      updateUrlWithMagicLinkState(newState);

      return newState;
    });
  };

  const handleWaitlistSubmit = (email: string) => {
    setCurrentUserEmail(email);

    addToWaitlist({
      variables: {
        input: {
          attributes: {
            email,
            zipcode: currentZipCode,
            storeSlug: storeSlug || "",
          },
        },
      },
    });
  };

  const renderAvailabilityForm = () => (
    <JourneyAvailabilityForm
      onContinue={handleContinue}
      loading={isProcessing}
      initialEmail={currentUserEmail}
      initialZipCode={currentZipCode}
    />
  );

  const renderLoginForm = () => (
    <JourneyLoginForm
      onLoginSuccess={handleLoginSuccess}
      onMagicLinkClick={handleMagicLinkClick}
      email={loginState?.email}
      storeSlug={storeSlug}
      storeId={storeId}
      journeyPackageId={journeyPackageId}
      loginState={loginState || undefined}
    />
  );

  const renderPatientInfoForm = () => (
    <JourneyPatientInfoForm
      onPatientCreated={handlePatientCreated}
      onSubmit={() => {
        void logAnalyticsEvent({
          variables: {
            input: {
              eventName: AnalyticsEvent.JOURNEY_CHECKOUT_PATIENT_SUBMIT,
              payload: {
                practitioner: analyticsPractitionerName ?? "",
                panel_selection: analyticsPanelName ?? "",
                panel_subtotal:
                  analyticsPanelSubtotal !== undefined
                    ? `$${analyticsPanelSubtotal.toFixed(2)}`
                    : "",
              },
            },
          },
        });
      }}
      email={patientInfoState?.email}
      storeSlug={storeSlug}
      storeId={storeId}
      journeyPackageId={journeyPackageId}
      patientInfoState={patientInfoState || undefined}
    />
  );

  const renderMagicLinkEmailForm = () => (
    <JourneyEmailFormContent
      email={magicLinkState?.email || ""}
      storeId={storeId}
      setEmail={handleMagicLinkEmailChange}
      setPage={page => {
        if (page === MAGIC_LINK_PAGES.SUCCESS) {
          handleMagicLinkSuccess();
        }
      }}
      onBackToLogin={() => updateMagicLinkState(null)}
      authenticatedRedirectPath={authenticatedRedirectPath}
    />
  );

  const renderMagicLinkSuccess = () => (
    <JourneySuccessContent
      email={magicLinkState?.email || ""}
      setPage={page => {
        if (page === MAGIC_LINK_PAGES.EMAIL) {
          setMagicLinkState(prev => (prev ? { ...prev, page: MAGIC_LINK_PAGES.EMAIL } : null));
        }
      }}
      isSwiftFillSignIn={false}
      onBackToLogin={() => updateMagicLinkState(null)}
    />
  );

  const renderOrderSummary = () => <JourneyOrderSummary journeyPackageId={journeyPackageId} />;

  const renderLeftContent = () => {
    if (magicLinkState) {
      if (magicLinkState.page === MAGIC_LINK_PAGES.EMAIL) {
        return renderMagicLinkEmailForm();
      } else {
        return renderMagicLinkSuccess();
      }
    }

    if (patientInfoState) {
      return renderPatientInfoForm();
    }

    if (loginState) {
      return renderLoginForm();
    }

    return renderAvailabilityForm();
  };

  return (
    <div css={styles.pageContainer}>
      <JourneyCheckoutHeaderNav
        onBack={
          loginState || patientInfoState || magicLinkState ? handleBackNavigation : handleBackToPLP
        }
      />
      <div css={styles.contentArea}>
        {isMobile ? (
          <div css={styles.mobileLayout}>
            <JourneyMobileOrderSummary orderTotal={orderTotal} isLoading={journeyPackageLoading}>
              {renderOrderSummary()}
            </JourneyMobileOrderSummary>
            {renderLeftContent()}
          </div>
        ) : (
          <JourneyCheckoutLayout
            leftContent={renderLeftContent()}
            rightContent={renderOrderSummary()}
          />
        )}
      </div>
      <PageFooter type="checkout" hideShippingDisclaimer />
      <JourneyWaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
        prefilledEmail={currentUserEmail}
        isLoading={addToWaitlistLoading}
        onSubmit={handleWaitlistSubmit}
        waitlistReason={waitlistReason}
        dispensaryName={dispensaryName}
      />
    </div>
  );
};

export { JourneyCheckoutPage };
